import { useCallback } from 'react';
import type { KeyValuePair } from '@invision/core';
import { SessionHelpers, transformKeyValueArrayToObject } from '@invision/core';
import { useRetrieveSessionMutation } from '../apis/useRetrieveSessionMutation';
import { isSavableResumeUrl } from '../utils/resumeUrlHelpers';

/**
 * Returns a function that builds the preference set to persist when logging out,
 * with the "resume last URL" preference (`${systemId}-LAST-URL`) upserted for the
 * active business unit, so the user is returned there after re-authenticating.
 *
 * Every logout (React header, React idle timer, and the Angular modules) hands
 * off to this `/logout` page with the current page as a `lastUrl` query param
 * (present only when it should be saved). This hook retrieves the current
 * session, merges the resume URL into the existing preferences, and returns the
 * full set so it can be persisted **as part of closing the session**
 * (`Security/CloseSession` accepts preferences to save) rather than via a
 * separate `UpdatePreferences` round-trip.
 *
 * The `/logout` page is a cold load with no populated Redux store, so the
 * session is retrieved here to obtain the current preferences and system id.
 *
 * Best-effort: when `lastUrl` is absent or points at an auth-app route, or when
 * the server session is already expired (a 401 on retrieve, e.g. on idle
 * timeout), `undefined` is returned and logout continues without a resume
 * preference.
 *
 * Endpoint: Security/RetrieveSession
 */
export function useSaveResumeUrlOnLogout() {
  const { mutateAsync: retrieveSession } = useRetrieveSessionMutation();

  return useCallback(
    async (
      lastUrl: string | null | undefined
    ): Promise<KeyValuePair<string>[] | undefined> => {
      if (!isSavableResumeUrl(lastUrl)) {
        return undefined;
      }

      try {
        const { SystemId, SessionSummary } = await retrieveSession({});

        if (!SystemId) {
          return undefined;
        }

        const preferences = transformKeyValueArrayToObject(
          SessionSummary?.Preferences
        );

        return SessionHelpers.buildPreferencesWithLastUrl(
          preferences,
          SystemId,
          lastUrl
        );
      } catch {
        // Best-effort: a closed/expired server session (e.g. idle-timeout 401)
        // must never block logout. The resume URL is simply not saved.
        return undefined;
      }
    },
    [retrieveSession]
  );
}
